import { tVariant } from "tmt_api/src/types/types.validator";

type tVariantOptions = {
  [key: string]: string[];
};

type tVariantInfo = {
  inventory: number;
  isPublished: boolean;
  maxMsrpPrice: number;
  maxWhosalePrice: number;
  minMsrpPrice: number;
  minWhosalePrice: number;
};

export const getVariantOptionValue = (variant: tVariant): string =>
  variant.options.reduce((acc, option) => `${acc}~${option.label}`, "").substring(1);

export const getVariantOptionsList = (variant?: tVariant): string[] =>
  variant?.options.map((option) => option.label) ?? [];

export const getVariantOptions = (variants: tVariant[]): tVariantOptions => {
  return variants.reduce<tVariantOptions>((acc, variant) => {
    let newAcc = {};
    variant.options.forEach((innerVariant) => {
      const variantOption = acc[innerVariant.name];
      if (!variantOption) {
        newAcc = { ...newAcc, [innerVariant.name]: [innerVariant.label] };
      } else {
        const added = variantOption.findIndex((option) => option === innerVariant.label) !== -1;
        if (!added) {
          newAcc = { ...newAcc, [innerVariant.name]: [...variantOption, innerVariant.label] };
        }
      }
    });
    return { ...acc, ...newAcc };
  }, {});
};

export const findVariantByOptionValue = (variants: tVariant[], optionValue: string): tVariant | undefined => {
  return variants.find((variant) => getVariantOptionValue(variant) === optionValue);
};

export const findVariantByOptionList = (variants: tVariant[], optionList: string[]): tVariant | undefined => {
  return variants.find((variant) => {
    const optionValue = getVariantOptionValue(variant);
    return optionList.every((optionListItem) => (optionListItem ? optionValue.includes(optionListItem) : false));
  });
};

export const isVariantAvailableByOptionValue = (
  variants: tVariant[],
  optionList: string[],
  positionValue: [position: number, value: string],
): boolean => {
  const position = positionValue[0];
  const value = positionValue[1];
  const newOptionsList = [...optionList.slice(0, position), value, ...optionList.slice(position + 1)];
  const variant = findVariantByOptionList(variants, newOptionsList);
  return variant ? variant.purchasable && !!variant.inventory : true;
};

const getFirstVariantAvailable = (variants: tVariant[]): tVariant | undefined => {
  let result;
  for (const variant of variants) {
    if (variant.purchasable && variant.inventory) {
      result = variant;
      break;
    }
  }
  return result;
};

export const getVariantsInfo = (variants: tVariant[]): tVariantInfo => {
  const firtAvailableVariant = getFirstVariantAvailable(variants);
  return variants.reduce<tVariantInfo>(
    (info, variant) => {
      const isAvailable = variant.purchasable && !!variant.inventory;
      return {
        inventory: variant.purchasable ? info.inventory + variant.inventory : info.inventory,
        isPublished: info.isPublished || variant.purchasable,
        maxMsrpPrice: isAvailable && variant.msrpPrice > info.maxMsrpPrice ? variant.msrpPrice : info.maxMsrpPrice,
        maxWhosalePrice:
          isAvailable && variant.wholesalePrice > info.maxWhosalePrice ? variant.wholesalePrice : info.maxWhosalePrice,
        minMsrpPrice: isAvailable && variant.msrpPrice < info.minMsrpPrice ? variant.msrpPrice : info.minMsrpPrice,
        minWhosalePrice:
          isAvailable && variant.wholesalePrice < info.minWhosalePrice ? variant.wholesalePrice : info.minWhosalePrice,
      };
    },
    {
      inventory: 0,
      isPublished: false,
      maxMsrpPrice: firtAvailableVariant?.msrpPrice ?? 0,
      maxWhosalePrice: firtAvailableVariant?.wholesalePrice ?? 0,
      minMsrpPrice: firtAvailableVariant?.msrpPrice ?? 0,
      minWhosalePrice: firtAvailableVariant?.wholesalePrice ?? 0,
    },
  );
};
